/** @format */
import {
    Component,
    DestroyRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    signal,
    ViewChild,
    ViewEncapsulation,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { FudisValidators, NotificationComponent } from '@funidata/ngx-fudis';
import { TranslocoService } from '@ngneat/transloco';
import {
    CourseUnit,
    CreditRange,
    CustomCourseUnitAttainment,
    DegreeProgramme,
    GroupingModule,
    OtmId,
    Plan,
    Rule,
    StudyModule,
    StudyRight,
} from 'common-typescript/types';
import { Observable, ReplaySubject, shareReplay, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
    AppErrorHandler,
    PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
    PlanActionsService,
    PlanData,
    PlanRuleSelectionService,
    SisFormBuilder,
} from 'sis-components';
import { PlanDataService, PlanStructureData } from '../../plan-data.service';
import { createAddToPlanWizardStateService } from '../store/add-to-plan-wizard-utils';

interface AddToPlanForm {
    module: FormControl<string>;
}

interface WizardFormValidationError {
    message: string;
    elementId: string;
}

type ModuleWithRules = (StudyModule | DegreeProgramme | GroupingModule) & {
    hasAnyCourseUnitRule?: boolean;
    allRules?: Rule[];
};

interface RuleExtended extends Rule {
    courseUnitGroupId?: OtmId;
    rules?: RuleExtended[];
}

@Component({
    selector: 'app-add-to-plan-wizard-select-destination-step',
    standalone: false,
    templateUrl: './add-to-plan-wizard-select-destination-step.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class AddToPlanWizardSelectDestinationStepComponent implements OnInit {
    @Input() courseUnit?: CourseUnit | null;
    @Input() initialPlanId: OtmId;
    @Input() customCourseUnitAttainment?: CustomCourseUnitAttainment | null;
    @Output() initPlanManager = new EventEmitter<PlanStructureData>();
    @Output() setPlan = new EventEmitter<PlanStructureData>();
    @Output() next = new EventEmitter<void>();

    @ViewChild('planWizardErrorSummary') planWizardErrorSummary: NotificationComponent;
    @ViewChild('planWizardErrorNotification') planWizardErrorNotification: NotificationComponent;

    dataInput$: Observable<[OtmId, Plan[], StudyRight[]]>;
    planIdInputSubject$ = new ReplaySubject<OtmId>(1);
    form: FormGroup<AddToPlanForm>;
    planStructureData: WritableSignal<PlanStructureData> = signal(null);
    loadingState: WritableSignal<boolean> = signal(false);
    nextHasBeenClicked: boolean = false;

    wizardStateService = createAddToPlanWizardStateService();

    constructor(
        @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private readonly planActionsService: PlanActionsService,
        private readonly fb: SisFormBuilder,
        private readonly planDataService: PlanDataService,
        private readonly translocoService: TranslocoService,
        private readonly destroyRef: DestroyRef,
        private readonly appErrorHandler: AppErrorHandler,
        private readonly planRuleSelectionService: PlanRuleSelectionService,
    ) {}

    ngOnInit() {
        if (this.initialPlanId) {
            this.loadingState.set(true);

            const stateValue = this.wizardStateService.store.getValue();
            const planId = stateValue?.planStructureData?.plan?.id ?? this.initialPlanId;
            this.planIdInputSubject$.next(planId);
            this.dataInput$ = this.planDataService.createInputDataObservableFromPlanIdInput(this.planIdInputSubject$.asObservable());
            this.buildForm();
            this.planDataService
                .createDataObservable(this.dataInput$)
                .pipe(
                    shareReplay({
                        bufferSize: 1,
                        refCount: true,
                    }),
                    takeUntilDestroyed(this.destroyRef),
                    catchError(err => {
                        this.loadingState.set(false);
                        return throwError(() => err);
                    }),
                    this.appErrorHandler.defaultErrorHandler(),
                )
                .subscribe(currentData => {
                    // Initialisation
                    if (!this.planStructureData()) {
                        this.initPlanManager.emit(currentData);
                    }
                    this.planStructureData.set(currentData);
                    this.setPlan.emit(currentData);
                    this.loadingState.set(false);
                });
        }
    }

    buildForm() {
        const selectedModule = this.wizardStateService.store.getValue().selectedModule?.id;
        this.form = this.fb.group({
            module: this.fb.control(selectedModule ?? null, [
                FudisValidators.required(this.translocoService.translate('ADD_TO_PLAN_WIZARD.VALIDATION.NO_MODULE_SELECTED')),
            ]),
        });
    }

    nextClicked(): void {
        if (this.initialPlanId && this.planStructureData() && (this.courseUnit || this.customCourseUnitAttainment)) {
            this.form.markAllAsTouched();
            this.nextHasBeenClicked = true;
            if (this.form.valid) {
                const selectedModule = this.planStructureData().planStateAndData.planData.modulesById[this.form.controls.module.value];
                if (this.courseUnit) {
                    this.planActionsService.selectCourseUnit(this.courseUnit, selectedModule);
                } else {
                    this.planActionsService.selectCustomCourseUnitAttainment(this.customCourseUnitAttainment, selectedModule);
                }
                this.wizardStateService.store.update(state => ({
                    ...state,
                    planStructureData: this.planStructureData(),
                    selectedModule,
                    courseUnit: this.courseUnit,
                    customCourseUnitAttainment: this.customCourseUnitAttainment,
                }));
                this.next.emit();
            } else {
                setTimeout(() => {
                    this.planWizardErrorSummary?.articleElement?.nativeElement?.focus();
                });
            }
        }
        // Focus on the error notification
        else {
            this.setFocusToNotification();
        }
    }

    isFormInvalidOnNextClick(): boolean {
        return this.nextHasBeenClicked && !this.form.controls.module.valid;
    }

    collectValidationErrors() {
        const errors: WizardFormValidationError[] = [];
        if (this.form.controls.module.errors?.required?.message) {
            errors.push({ message: this.form.controls.module.errors.required.message, elementId: 'moduleSelector' });
        }
        return errors;
    }

    protected setFocusToNotification(): void {
        this.planWizardErrorNotification?.articleElement?.nativeElement?.focus();
    }

    handlePlanChange(selectedPlanId: OtmId): void {
        // Reset selected module to make sure selected module always belongs to the selected plan
        this.moduleGroupControl.setValue(null);
        this.planIdInputSubject$.next(selectedPlanId);
    }

    getModule(id: OtmId, planData: PlanData): StudyModule | DegreeProgramme | GroupingModule {
        return planData.modulesById[id] as StudyModule | DegreeProgramme | GroupingModule;
    }

    getModuleIds(data: PlanStructureData) {
        return Object.keys(this.filterModules(data));
    }

    getCreditRange(credits: number | CreditRange) {
        return typeof credits === 'number' ? { min: credits, max: credits } : credits;
    }

    get moduleGroupControl() {
        return this.form?.controls['module'];
    }

    private filterModules(data: PlanStructureData): Record<OtmId, ModuleWithRules> {
        const filteredModules: Record<OtmId, ModuleWithRules> = {};
        Object.values(data.planStateAndData.planData.modulesById).forEach((mod: ModuleWithRules) => {
            if (
                ((this.courseUnit && !data.planStateAndData.planStateObject?.courseUnits[this.courseUnit.id]?.isInPlan) ||
                    (this.customCourseUnitAttainment &&
                        !data.planStateAndData.planStateObject?.customCourseUnitAttainments[this.customCourseUnitAttainment.id]
                            ?.isInPlan)) &&
                data.planStateAndData.planStateObject?.modules[mod.id]?.moduleValidationResult?.state !== 'ATTAINED' &&
                (mod.hasAnyCourseUnitRule || (this.courseUnit && this.isRuleForMatchingCourseUnitGroupIdFound(mod, data)))
            ) {
                filteredModules[mod.id] = mod;
            }
        });
        return filteredModules;
    }

    private isRuleForMatchingCourseUnitGroupIdFound(mod: ModuleWithRules, data: PlanStructureData): boolean {
        const foundRule = mod.allRules?.find(
            (rule: RuleExtended) =>
                (rule.courseUnitGroupId === this.courseUnit.groupId ||
                    rule.rules?.find((r: RuleExtended) => r.courseUnitGroupId === this.courseUnit.groupId) !== undefined) &&
                !this.planRuleSelectionService.isCourseUnitSelectionDisabled(
                    this.courseUnit,
                    mod,
                    data.validatablePlan,
                    data.planStateAndData.planStateObject,
                ),
        );
        return foundRule !== undefined;
    }
}
